/*
 * Hover quick-add overlay (Huckberry-style) — 2-step size flow.
 *
 * Each .js-quick-add-overlay block on the page is one product's overlay. Inside:
 *   - a Shopify product form (action=/cart/add) that's already wired into the
 *     theme's existing ajaxCart plugin via the .js-product-form class
 *   - a JSON variant map: [[variant_id, color_value, size_value, available], ...]
 *   - one button.js-quick-add-pill per size value (for products with sizes),
 *     OR a single submit button (for products without)
 *
 * Click flow (sized products):
 *   1. User clicks a size pill
 *   2. We resolve (currentColor × clickedSize) → variant_id via the variant map
 *   3. The pill grid is swapped for an "Add to Cart" button labeled with the
 *      selected size. The user clicks that button to actually add.
 *   4. Add to cart button submits the form — ajaxCart intercepts, posts to
 *      /cart/add, opens drawer. The overlay then resets to show pills again.
 *
 * Color sync:
 *   The collection card already has a color-swatch row that swaps the card image
 *   on click (ProductCardSwatches in theme.js). We hook the same click events to
 *   update each overlay's "current color" so the next size-pill click resolves
 *   to the right (color × size) variant.
 */
(function() {
  "use strict";

  function $$(sel, parent) {
    return Array.prototype.slice.call((parent || document).querySelectorAll(sel));
  }

  function setTriggerState(trigger, open) {
    if (!trigger) return;
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
    var label = open ? trigger.dataset.closeLabel : trigger.dataset.openLabel;
    if (label) trigger.setAttribute("aria-label", label);
  }

  function readVariantMap(overlay) {
    var node = overlay.querySelector(".js-quick-add-variants");
    if (!node) return [];
    try {
      var data = JSON.parse(node.textContent);
      // Each row: [id, color, size, available]
      return data.map(function(row) {
        return { id: row[0], color: row[1] || "", size: row[2] || "", available: !!row[3] };
      });
    } catch (e) {
      console.warn("quick-add-overlay: bad variant map", e);
      return [];
    }
  }

  function refreshPillState(overlay) {
    var color = overlay.dataset.currentColor || "";
    var variants = readVariantMap(overlay);
    var pills = $$(".js-quick-add-pill", overlay);

    pills.forEach(function(pill) {
      var size = pill.dataset.size || "";
      // Find a variant matching (color, size). If multiple variants match
      // (shouldn't happen in a normal catalog), prefer an available one.
      var match = null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v.color === color && v.size === size) {
          if (!match || v.available) match = v;
        }
      }
      if (!match) {
        pill.disabled = true;
        pill.removeAttribute("data-variant-id");
        pill.title = "Unavailable";
        return;
      }
      if (!match.available) {
        pill.disabled = true;
        pill.dataset.variantId = String(match.id);
        pill.title = "Sold out";
      } else {
        pill.disabled = false;
        pill.dataset.variantId = String(match.id);
        pill.title = "";
      }
    });
  }

  function setCurrentColor(overlay, color) {
    overlay.dataset.currentColor = color || "";
    // Color change invalidates any pending size selection — reset to pills.
    resetSelection(overlay);
    refreshPillState(overlay);
    // If a sidebar size filter is active, re-apply it for the new color
    // (the variant id will differ from the previous color's variant id).
    autoSelectFromSidebar(overlay, getActiveSidebarSizes());
  }

  function ensureConfirmUI(overlay) {
    // Confirm UI is now rendered server-side by product-grid-item.liquid.
    // Fall back to JS injection if an older card markup is on the page.
    var confirm = overlay.querySelector(".js-quick-add-confirm");
    if (confirm) return confirm;
    var form = overlay.querySelector(".js-product-form");
    if (!form) return null;
    confirm = document.createElement("div");
    confirm.className = "quick-add-overlay__confirm js-quick-add-confirm";
    confirm.innerHTML =
      '<button type="button" class="quick-add-overlay__btn quick-add-overlay__btn--confirm js-quick-add-confirm-btn">' +
        '<span class="js-quick-add-confirm-label">Add to Cart</span>' +
      '</button>' +
      '<button type="button" class="quick-add-overlay__change js-quick-add-change" aria-label="Change size">Change size</button>';
    form.appendChild(confirm);
    return confirm;
  }

  function showConfirm(overlay, pill) {
    var confirm = ensureConfirmUI(overlay);
    if (!confirm) return;
    var label = confirm.querySelector(".js-quick-add-confirm-label");
    var size = pill.dataset.size || "";
    if (label) label.textContent = size ? "Add to Cart — " + size : "Add to Cart";
    overlay.dataset.selectedVariantId = pill.dataset.variantId || "";
    overlay.dataset.selectedSize = size;
    overlay.classList.add("is-size-selected");
  }

  function resetSelection(overlay) {
    overlay.classList.remove("is-size-selected");
    overlay.removeAttribute("data-selected-variant-id");
    overlay.removeAttribute("data-selected-size");
    var btn = overlay.querySelector(".js-quick-add-confirm-btn");
    if (btn) {
      btn.classList.remove("is-loading", "is-added", "is-error");
    }
  }

  function submitAdd(overlay, variantId, btn) {
    var form = overlay.querySelector(".js-product-form");
    if (!form) return;
    var idInput = form.querySelector(".js-quick-add-id");
    if (idInput) idInput.value = variantId;

    var label = btn.querySelector(".js-quick-add-confirm-label");
    var originalText = label ? label.textContent : btn.textContent;
    btn.classList.remove("is-added", "is-error");
    btn.classList.add("is-loading");
    if (label) label.textContent = "Adding…";

    var $ = window.jQuery;
    var done = function(success) {
      btn.classList.remove("is-loading");
      btn.classList.add(success ? "is-added" : "is-error");
      if (label) label.textContent = success ? "Added ✓" : "Try again";
      setTimeout(function() {
        btn.classList.remove("is-added", "is-error");
        if (label) label.textContent = originalText;
        // After a successful add, return to size pills so the next hover starts fresh.
        if (success) resetSelection(overlay);
      }, success ? 1400 : 1800);
    };

    if ($) {
      $("body").one("afterAddItem.ajaxCart", function() {
        done(true);
        setTimeout(function() {
          // Trigger the post-animation hook manually so the existing handler
          // (theme.js) runs and opens the cart drawer.
          $("body").trigger("afterButtonAnimation.ajaxCart", [null, true]);
        }, 350);
      });
      $("body").one("ajaxError.ajaxCart afterCartLoadFail.ajaxCart", function() {
        done(false);
      });
    } else {
      setTimeout(function() { done(true); }, 1200);
    }

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  function handlePillClick(overlay, pill) {
    if (pill.disabled) return;
    // Primary: use the variant id refreshPillState set on this pill for the
    // current color. If that's missing (e.g. variant map mismatch on case),
    // fall back to scanning the variant map for the first available variant
    // with this size.
    var variantId = pill.dataset.variantId;
    if (!variantId) {
      var size = (pill.dataset.size || "").toLowerCase();
      var variants = readVariantMap(overlay);
      var fallback = null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if ((v.size || "").toLowerCase() === size) {
          if (v.available) { fallback = v; break; }
          if (!fallback) fallback = v;
        }
      }
      if (fallback) {
        variantId = String(fallback.id);
        pill.dataset.variantId = variantId;
      }
    }
    if (!variantId) return;
    // Direct add (1-click) — the 2-step confirm UI is only used when the
    // sidebar pre-selects a size on page load.
    submitFromPill(overlay, pill, variantId);
  }

  function submitFromPill(overlay, pill, variantId) {
    var form = overlay.querySelector(".js-product-form");
    if (!form) return;
    var idInput = form.querySelector(".js-quick-add-id");
    if (idInput) idInput.value = variantId;

    var originalLabel = pill.textContent;
    pill.classList.remove("is-added", "is-error");
    pill.classList.add("is-loading");
    pill.textContent = "...";

    var $ = window.jQuery;
    var done = function(success) {
      pill.classList.remove("is-loading");
      pill.textContent = originalLabel;
      if (success) {
        pill.classList.add("is-added");
        overlay.classList.remove("is-open");
        var trigger = overlay.parentElement && overlay.parentElement.querySelector(".js-quick-add-mobile-trigger");
        if (trigger) trigger.setAttribute("aria-expanded", "false");
        setTimeout(function() { pill.classList.remove("is-added"); }, 1400);
      } else {
        pill.classList.add("is-error");
        setTimeout(function() { pill.classList.remove("is-error"); }, 1800);
      }
    };

    if ($) {
      $("body").one("afterAddItem.ajaxCart", function() {
        done(true);
        setTimeout(function() {
          $("body").trigger("afterButtonAnimation.ajaxCart", [null, true]);
        }, 350);
      });
      $("body").one("ajaxError.ajaxCart afterCartLoadFail.ajaxCart", function() {
        done(false);
      });
    } else {
      setTimeout(function() { done(true); }, 1200);
    }

    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.submit();
  }

  // Active size filter values, tried in this order:
  //   1. Server-rendered JSON script tag (#js-collection-active-sizes).
  //   2. The active filter chips in the DOM (annotated with
  //      data-filter-label/data-filter-value). Most reliable on collection
  //      and search pages because it reads exactly what Liquid rendered.
  //   3. URL params (?filter.v.option.size=Small etc.) — last-resort fallback.
  function getActiveSidebarSizes() {
    var sizes = [];

    // 1. Script tag
    var node = document.getElementById("js-collection-active-sizes");
    if (node && node.textContent && node.textContent.trim()) {
      try {
        var arr = JSON.parse(node.textContent);
        if (Array.isArray(arr)) {
          arr.forEach(function(s) {
            if (s) sizes.push(String(s).toLowerCase());
          });
        }
      } catch (e) { /* ignore parse errors */ }
    }
    if (sizes.length) return sizes;

    // 2. Active filter chips
    var chips = $$(".js-active-filter-chip");
    chips.forEach(function(chip) {
      var label = (chip.getAttribute("data-filter-label") || "").toLowerCase();
      var value = (chip.getAttribute("data-filter-value") || "").toLowerCase();
      if (label.indexOf("size") !== -1 && value) sizes.push(value);
    });
    if (sizes.length) return sizes;

    // 3. URL params
    try {
      var params = new URLSearchParams(window.location.search);
      params.forEach(function(value, key) {
        var k = key.toLowerCase();
        if (k.indexOf("filter.") === 0 && k.indexOf("size") !== -1) {
          if (value) sizes.push(value.toLowerCase());
        }
      });
    } catch (e) { /* ignore */ }
    return sizes;
  }

  function autoSelectFromSidebar(overlay, activeSizes) {
    if (!activeSizes.length) return;
    // Find the first matching pill that's available for the current color.
    var pills = $$(".js-quick-add-pill", overlay);
    for (var i = 0; i < pills.length; i++) {
      var pill = pills[i];
      if (pill.disabled) continue;
      var size = (pill.dataset.size || "").toLowerCase();
      if (activeSizes.indexOf(size) !== -1) {
        showConfirm(overlay, pill);
        return;
      }
    }
  }

  // Shopify's storefront size filter shows products that HAVE the size as a
  // variant — it doesn't check inventory, so sold-out-in-that-size products
  // still appear. Hide the grid cell for those.
  function hideIfNoStockForActiveSize(overlay, activeSizes) {
    if (!activeSizes.length) return;
    var variants = readVariantMap(overlay);
    if (!variants.length) return;
    var hasAvailable = false;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var size = (v.size || "").toLowerCase();
      if (v.available && activeSizes.indexOf(size) !== -1) {
        hasAvailable = true;
        break;
      }
    }
    if (hasAvailable) return;
    var cell = overlay.closest(".o-layout__item") || overlay.closest(".product-card");
    if (cell) {
      cell.classList.add("is-out-of-stock-for-size");
      cell.style.display = "none";
    }
  }

  // Wire a single overlay. Idempotent — guards via a flag so re-running on
  // ajax-reloaded sections doesn't double-bind clicks.
  function initOverlay(overlay, activeSidebarSizes) {
    if (overlay.__quickAddWired) {
      // Already wired; just re-apply auto-select in case filter changed.
      refreshPillState(overlay);
      resetSelection(overlay);
      autoSelectFromSidebar(overlay, activeSidebarSizes);
      hideIfNoStockForActiveSize(overlay, activeSidebarSizes);
      return;
    }
    overlay.__quickAddWired = true;

    refreshPillState(overlay);
    ensureConfirmUI(overlay);
    autoSelectFromSidebar(overlay, activeSidebarSizes);
    hideIfNoStockForActiveSize(overlay, activeSidebarSizes);

    $$(".js-quick-add-pill", overlay).forEach(function(pill) {
      pill.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        handlePillClick(overlay, pill);
      });
    });

    overlay.addEventListener("click", function(e) {
      var confirmBtn = e.target.closest(".js-quick-add-confirm-btn");
      if (confirmBtn) {
        e.preventDefault();
        e.stopPropagation();
        var variantId = overlay.dataset.selectedVariantId;
        if (!variantId) return;
        submitAdd(overlay, variantId, confirmBtn);
        return;
      }
      var change = e.target.closest(".js-quick-add-change");
      if (change) {
        e.preventDefault();
        e.stopPropagation();
        resetSelection(overlay);
        return;
      }
    });

    var addBtn = overlay.querySelector(".js-quick-add-submit");
    if (addBtn) wireSubmitBtn(overlay, addBtn);

    var mobileTrigger = overlay.parentElement && overlay.parentElement.querySelector(".js-quick-add-mobile-trigger");
    if (mobileTrigger) {
      mobileTrigger.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        var willOpen = !overlay.classList.contains("is-open");
        $$(".js-quick-add-overlay.is-open").forEach(function(other) {
          if (other === overlay) return;
          other.classList.remove("is-open");
          var otherTrigger = other.parentElement && other.parentElement.querySelector(".js-quick-add-mobile-trigger");
          setTriggerState(otherTrigger, false);
        });
        overlay.classList.toggle("is-open", willOpen);
        setTriggerState(mobileTrigger, willOpen);
      });
    }
  }

  function wireSubmitBtn(overlay, addBtn) {
    addBtn.addEventListener("click", function(e) {
      e.preventDefault();
      e.stopPropagation();
      var form = overlay.querySelector(".js-product-form");
      if (!form) return;
      var originalLabel = addBtn.textContent;
      addBtn.classList.remove("is-added", "is-error");
      addBtn.classList.add("is-loading");
      addBtn.textContent = "Adding…";

      var $ = window.jQuery;
      var done = function(success) {
        addBtn.classList.remove("is-loading");
        addBtn.textContent = success ? "Added ✓" : "Try again";
        addBtn.classList.add(success ? "is-added" : "is-error");
        setTimeout(function() {
          addBtn.classList.remove("is-added", "is-error");
          addBtn.textContent = originalLabel;
        }, success ? 1400 : 1800);
      };
      if ($) {
        $("body").one("afterAddItem.ajaxCart", function() {
          done(true);
          setTimeout(function() {
            $("body").trigger("afterButtonAnimation.ajaxCart", [null, true]);
          }, 350);
        });
        $("body").one("ajaxError.ajaxCart afterCartLoadFail.ajaxCart", function() {
          done(false);
        });
      } else {
        setTimeout(function() { done(true); }, 1200);
      }

      if (typeof form.requestSubmit === "function") form.requestSubmit();
      else form.submit();
    });
  }

  function init() {
    var overlays = $$(".js-quick-add-overlay");
    var activeSidebarSizes = getActiveSidebarSizes();
    overlays.forEach(function(overlay) {
      initOverlay(overlay, activeSidebarSizes);
    });

    // Re-init when ajax-loaded sections add overlays later (filter changes,
    // pagination, infinite scroll). Idempotent on already-wired overlays.
    var mo = new MutationObserver(function(mutations) {
      var sizes = null;
      mutations.forEach(function(m) {
        m.addedNodes.forEach(function(n) {
          if (n.nodeType !== 1) return;
          var candidates = [];
          if (n.classList && n.classList.contains("js-quick-add-overlay")) candidates.push(n);
          if (n.querySelectorAll) {
            n.querySelectorAll(".js-quick-add-overlay").forEach(function(o) { candidates.push(o); });
          }
          if (candidates.length) {
            if (sizes === null) sizes = getActiveSidebarSizes();
            candidates.forEach(function(o) { initOverlay(o, sizes); });
          }
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Color sync: when the user clicks a color swatch on the card, the
    // existing ProductCardSwatches handler swaps the image. We piggyback on
    // the same click — find which card the swatch belongs to, then update
    // that card's overlay current-color from the swatch's aria-label.
    document.addEventListener("click", function(e) {
      var swatchItem = e.target.closest(".js-product-swatch-item");
      if (!swatchItem) return;
      var card = swatchItem.closest(".js-product-card");
      if (!card) return;
      var overlay = card.querySelector(".js-quick-add-overlay");
      if (!overlay) return;
      var swatch = swatchItem.querySelector("variant-swatch");
      var label = swatch && swatch.getAttribute("aria-label");
      if (label) setCurrentColor(overlay, label);
    }, true);

    document.addEventListener("click", function(e) {
      $$(".js-quick-add-overlay.is-open").forEach(function(overlay) {
        var card = overlay.closest(".product-card");
        if (card && card.contains(e.target)) return;
        overlay.classList.remove("is-open");
        var trigger = overlay.parentElement && overlay.parentElement.querySelector(".js-quick-add-mobile-trigger");
        setTriggerState(trigger, false);
      });
    });

    document.addEventListener("keydown", function(e) {
      if (e.key !== "Escape") return;
      $$(".js-quick-add-overlay.is-open").forEach(function(overlay) {
        overlay.classList.remove("is-open");
        var trigger = overlay.parentElement && overlay.parentElement.querySelector(".js-quick-add-mobile-trigger");
        setTriggerState(trigger, false);
        if (trigger) trigger.focus();
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
